import { useCallback, useEffect, useState } from "react";
import { Button, Flex, Text } from "@chakra-ui/react";
import { LuRefreshCw } from "react-icons/lu";

declare const __BUILD_VERSION__: string;

export function useAppUpdate() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [swVersion, setSwVersion] = useState<string | null>(null);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    // Check for updates every 60s
    const check = () => navigator.serviceWorker.ready.then((reg) => reg.update()).catch(() => {});
    const interval = setInterval(check, 60_000);

    // Listen for new SW
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      setUpdateAvailable(true);
    });

    // Detect waiting worker
    navigator.serviceWorker.ready.then((reg) => {
      if (reg.waiting) setUpdateAvailable(true);
      reg.addEventListener("updatefound", () => {
        const newWorker = reg.installing;
        newWorker?.addEventListener("statechange", () => {
          if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
            setUpdateAvailable(true);
          }
        });
      });
    });

    // Get current SW version
    navigator.serviceWorker.addEventListener("message", (e) => {
      if (e.data?.type === "VERSION") setSwVersion(e.data.version);
    });
    navigator.serviceWorker.ready.then((reg) => {
      reg.active?.postMessage("GET_VERSION");
    });

    return () => clearInterval(interval);
  }, []);

  const applyUpdate = useCallback(() => {
    navigator.serviceWorker.ready.then((reg) => {
      if (reg.waiting) reg.waiting.postMessage("SKIP_WAITING");
    });
    window.location.reload();
  }, []);

  const forceRefresh = useCallback(async () => {
    // Nuclear option: unregister SW, clear all caches, reload
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
    window.location.reload();
  }, []);

  return {
    updateAvailable,
    applyUpdate,
    forceRefresh,
    buildVersion: typeof __BUILD_VERSION__ !== "undefined" ? __BUILD_VERSION__ : "dev",
    swVersion,
  };
}

export function UpdateBanner() {
  const { updateAvailable, applyUpdate } = useAppUpdate();

  if (!updateAvailable) return null;

  return (
    <Flex
      position="fixed"
      bottom="4"
      left="50%"
      transform="translateX(-50%)"
      bg="primary.500"
      color="white"
      px="4"
      py="2"
      borderRadius="full"
      align="center"
      gap="3"
      boxShadow="lg"
      zIndex="toast"
    >
      <Text fontSize="sm" fontWeight="bold">Update available</Text>
      <Button size="xs" colorScheme="whiteAlpha" onClick={applyUpdate} leftIcon={<LuRefreshCw size={12} />}>
        Update now
      </Button>
    </Flex>
  );
}
